import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { twMerge } from 'tailwind-merge';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button, ButtonProps } from '../ui/button';
import MessageLoading from './message-loading';

// ChatBubble
const chatBubbleVariant = cva('group relative flex h-fit max-w-[80%] items-start gap-2', {
  variants: {
    variant: {
      received: 'self-start',
      sent: 'flex-row-reverse self-end',
    },
    layout: {
      default: '',
      ai: 'w-full max-w-full items-center',
    },
  },
  defaultVariants: {
    variant: 'received',
    layout: 'default',
  },
});

interface ChatBubbleProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof chatBubbleVariant> {}

const ChatBubble = React.forwardRef<HTMLDivElement, ChatBubbleProps>(({ className, variant, layout, children, ...props }, ref) => (
  <div className={twMerge(chatBubbleVariant({ variant, layout, className }), 'relative group')} ref={ref} {...props}>
    {React.Children.map(children, (child) =>
      React.isValidElement(child) && typeof child.type !== 'string'
        ? React.cloneElement(child, {
            variant,
            layout,
          } as React.ComponentProps<typeof child.type>)
        : child
    )}
  </div>
));
ChatBubble.displayName = 'ChatBubble';

// ChatBubbleAvatar
interface ChatBubbleAvatarProps {
  src?: string;
  fallback?: string;
  className?: string;
}

const ChatBubbleAvatar: React.FC<ChatBubbleAvatarProps> = ({ src, fallback, className }) => (
  <Avatar className={twMerge('self-end', className)}>
    <AvatarImage src={src} alt='Avatar' />
    <AvatarFallback>{fallback}</AvatarFallback>
  </Avatar>
);

// ChatBubbleMessage
const chatBubbleMessageVariants = cva('p-2 text-white-100', {
  variants: {
    variant: {
      received: 'rounded-r-xl rounded-tl-xl border border-gray-200 bg-white-100 text-black-100',
      sent: 'rounded-xl bg-brand-600',
    },
    layout: {
      default: '',
      ai: 'w-full rounded-none border-t bg-transparent',
    },
  },
  defaultVariants: {
    variant: 'received',
    layout: 'default',
  },
});

interface ChatBubbleMessageProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof chatBubbleMessageVariants> {
  isLoading?: boolean;
}

function LinkRenderer(props: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a href={props.href} target='_blank' rel='noreferrer'>
      {props.children}
    </a>
  );
}

const ChatBubbleMessage = React.forwardRef<HTMLDivElement, ChatBubbleMessageProps>(
  ({ className, variant, layout, isLoading = false, children, ...props }, ref) => (
    <div
      className={twMerge(
        chatBubbleMessageVariants({ variant, layout, className }),
        'break-words text-pretty max-w-full whitespace-pre-wrap text-sm [&_a]:text-brand-600'
      )}
      ref={ref}
      {...props}
    >
      {isLoading ? (
        <MessageLoading />
      ) : (
        <ReactMarkdown
          rehypePlugins={[rehypeRaw]}
          components={{
            ul: ({ node, ...props }) => <ul className='list-disc space-y-2 pl-3' {...props} />,
            ol: ({ node, ...props }) => <ol className='list-decimal space-y-2 pl-6' {...props} />,
            li: ({ node, ...props }) => <li className='ml-3' {...props} />,
            a: LinkRenderer,
          }}
        >
          {children as string}
        </ReactMarkdown>
      )}
    </div>
  )
);
ChatBubbleMessage.displayName = 'ChatBubbleMessage';

// ChatBubbleTimestamp
interface ChatBubbleTimestampProps extends React.HTMLAttributes<HTMLDivElement> {
  timestamp: string;
}

const ChatBubbleTimestamp: React.FC<ChatBubbleTimestampProps> = ({ timestamp, className, ...props }) => (
  <div className={twMerge('text-xs mt-2 text-right', className)} {...props}>
    {timestamp}
  </div>
);

// ChatBubbleAction
type ChatBubbleActionProps = ButtonProps & {
  icon: React.ReactNode;
};

const ChatBubbleAction: React.FC<ChatBubbleActionProps> = ({ icon, onClick, className, variant = 'ghost', size = 'icon', ...props }) => (
  <Button variant={variant} size={size} className={className} onClick={onClick} {...props}>
    {icon}
  </Button>
);

interface ChatBubbleActionWrapperProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'sent' | 'received';
  className?: string;
}

const ChatBubbleActionWrapper = React.forwardRef<HTMLDivElement, ChatBubbleActionWrapperProps>(({ variant, className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={twMerge(
      'absolute top-1/2 -translate-y-1/2 flex opacity-0 group-hover:opacity-100 transition-opacity duration-200',
      variant === 'sent' ? '-left-1 -translate-x-full flex-row-reverse' : '-right-1 translate-x-full',
      className
    )}
    {...props}
  >
    {children}
  </div>
));
ChatBubbleActionWrapper.displayName = 'ChatBubbleActionWrapper';

export {
  ChatBubble,
  ChatBubbleAction,
  ChatBubbleActionWrapper,
  ChatBubbleAvatar,
  ChatBubbleMessage,
  chatBubbleMessageVariants,
  ChatBubbleTimestamp,
  chatBubbleVariant,
};
